import type { MsdfFont } from './MsdfFont';

/**
 * One laid-out glyph quad relative to the text's anchor origin. The anchor
 * sits at the top of the line (BMFont convention). All coordinates are in
 * **display pixels** at the requested fontSize (TextLayout converts atlas
 * pixels → display pixels using `fontSize / font.lineHeight`).
 *
 * UVs are in [0, 1] across the atlas — the OverlayPass shader samples directly.
 */
export interface LaidOutGlyph {
  /** Quad top-left x in display pixels (relative to text origin). */
  x: number;
  /** Quad top-left y in display pixels (relative to text origin). */
  y: number;
  /** Quad width in display pixels. */
  width: number;
  /** Quad height in display pixels. */
  height: number;
  /** UV origin (top-left) in atlas space. */
  uvX: number;
  uvY: number;
  /** UV size (width, height) in atlas space. */
  uvW: number;
  uvH: number;
}

export interface LayoutResult {
  glyphs: LaidOutGlyph[];
  /** Total advance — useful for horizontal centering. */
  width: number;
  /** Line height in display pixels (single-line). */
  height: number;
}

export interface LayoutOptions {
  /** Desired line height in display pixels. The atlas metrics scale to this. */
  fontSize: number;
}

/**
 * Lays out a single line of text left-to-right, applying glyph metrics and
 * kerning from the font. Unknown codepoints fall back to space (or skip).
 *
 * No line breaking, no rich text, no bidi — single-line strings only for now.
 */
export function layoutText(font: MsdfFont, text: string, opts: LayoutOptions): LayoutResult {
  const scale = opts.fontSize / font.lineHeight;
  const glyphs: LaidOutGlyph[] = [];
  let penX = 0;
  let prevCode = 0;

  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    const glyph = font.getGlyph(code);
    if (!glyph) {
      // Unknown glyph — advance by a quarter em-space as a fallback so the
      // missing char doesn't collapse the surrounding layout.
      penX += font.fontSize * 0.25 * scale;
      prevCode = 0;
      continue;
    }

    if (prevCode !== 0) {
      penX += font.getKerning(prevCode, code) * scale;
    }

    if (glyph.width > 0 && glyph.height > 0) {
      glyphs.push({
        x: penX + glyph.xoffset * scale,
        y: glyph.yoffset * scale,
        width: glyph.width * scale,
        height: glyph.height * scale,
        uvX: glyph.x / font.atlasWidth,
        uvY: glyph.y / font.atlasHeight,
        uvW: glyph.width / font.atlasWidth,
        uvH: glyph.height / font.atlasHeight,
      });
    }

    penX += glyph.xadvance * scale;
    prevCode = code;
  }

  return { glyphs, width: penX, height: font.lineHeight * scale };
}
